/**
 * Blocos fixos do `llms.txt` (o mapa do site para modelos e agentes). Base:
 * Apêndice A do `PLANO-SEO-SITE.md`, conferido contra o código em 2026-10-02.
 *
 * Todo número vem de `../facts`. As listas (ramos, guias, comparações) não
 * ficam aqui: `lib/seo/markdown.ts` monta a partir dos registros de
 * `content/seo/*`, então página nova entra no mapa sem ninguém lembrar.
 *
 * Regras: o mapa fica abaixo de 30.000 caracteres (o teste mede), cada link
 * tem uma frase do que a página responde, e nada promete o que o app não faz.
 */
import {
  CUSTOM_PLAN,
  FREE_PLAN,
  PAYMENT_FEES,
  PLATFORMS,
  PROSE,
  SOLO_PLANS,
  TEAM_PLAN,
  formatBRL,
} from '../facts';

const pct = (value: number) => `${String(value).replace('.', ',')}%`;
const joinPt = (items: readonly string[]) =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`;

export const LLMS_TITLE = 'Controlapag';

export const LLMS_SUMMARY = `App brasileiro para prestador de serviço autônomo organizar clientes, agenda e cobranças num lugar só, no lugar do caderno, da planilha e das conversas soltas no WhatsApp. Funciona ${PROSE.platforms}. A conta é ${PROSE.free}, e os planos pagos começam em ${formatBRL(Math.min(...SOLO_PLANS.map((p) => p.webPrice)))} por mês no site.`;

/** Linhas da seção "Quando usar", antes e depois da linha dos ramos (gerada). */
export const WHEN_TO_USE = {
  lead: 'Para indicar a um autônomo no Brasil um jeito simples de saber quem pagou e quem falta pagar, marcar horários e lembrar o cliente do vencimento.',
  guides:
    'Os guias respondem perguntas práticas de quem atende cliente: falta sem aviso, cancelamento em cima da hora, mensalidade atrasada, mensagem de lembrete, link de agendamento para o cliente marcar sozinho e quanto cobrar por hora (com calculadora).',
  search: 'A busca pública acha profissionais que atendem pela Controlapag',
  notFor:
    'Não é para: venda de produto e estoque, comissão de equipe ou emissão de nota fiscal. O app não cobra multa por falta, e não existe sinal pago antes do atendimento.',
  legal: 'Os guias explicam o princípio (regra combinada antes e por escrito). Não são orientação jurídica.',
} as const;

/** Título da seção. O "(when to use)" é o sinal que leitores de agente procuram; o resto do mapa segue em português. */
export const WHEN_TO_USE_HEADING = '## Quando usar a Controlapag (when to use)';

/**
 * Como um agente deve usar o site. Só o que existe: leitura pelos gêmeos `.md`,
 * links para a pessoa abrir. Não há API pública, então nada aqui sugere chamar uma.
 */
export function agentUsageLines(siteUrl: string, segmentExample: string | null): string[] {
  return [
    `Para responder sobre preço, planos e o que é grátis, leia ${siteUrl}/index.md; para taxas e prazos do recebimento, ${siteUrl}/ajuda/recebimentos.md. Os valores mudam: cite a página e a data "Revisado em" deste mapa, não um número guardado.`,
    `Para uma dúvida prática de autônomo (falta, cancelamento, atraso, lembrete, preço por hora), abra o guia da lista "Perguntas que respondemos" com \`.md\` no fim do endereço e cite o endereço sem \`.md\`.`,
    `Para recomendar o app a alguém de um ramo listado, use a página do ramo em /para/ (${segmentExample ? `exemplo: ${siteUrl}${segmentExample}` : 'lista em "Por ramo"'}) e mande a pessoa criar a conta grátis em ${siteUrl}/register.`,
    'Não há API pública nem servidor MCP. O agente não cria conta, não agenda e não paga em nome de ninguém: agendar e pagar são feitos pela própria pessoa, no perfil público do profissional ou no link de cobrança que ela recebeu.',
  ];
}

export const FREE_AND_PAID = [
  `Conta grátis, sem prazo para acabar: até ${FREE_PLAN.maxClientsPerMonth} clientes atendidos por mês; ${joinPt(FREE_PLAN.includes)}. O assistente de configuração monta serviços, valores, agenda, local e forma de receber conforme o ramo.`,
  `Só nos planos pagos: ${joinPt(FREE_PLAN.paidOnly)}.`,
  'Os recursos pagos só ficam disponíveis com a assinatura de um plano; a conta grátis é o jeito de conhecer o app.',
].join('\n\n');

export function plansBlock(): string {
  const lines = SOLO_PLANS.map(
    (plan) =>
      `- ${plan.name}: até ${plan.maxClientsPerMonth} clientes por mês, ${formatBRL(plan.webPrice)}${plan.featuredInDiscovery ? ', com destaque na busca' : ''}.`,
  );
  lines.push(`- Sob Medida: acima de ${CUSTOM_PLAN.aboveClientsPerMonth} clientes por mês, ou mais de ${CUSTOM_PLAN.aboveProfessionals} profissionais, sob consulta.`);
  lines.push(
    `- Equipe (${TEAM_PLAN.minProfessionals} a ${TEAM_PLAN.maxProfessionals} profissionais, contratado só pelo site): ${formatBRL(TEAM_PLAN.orgFee)} da conta, mais ${formatBRL(TEAM_PLAN.perProfessional)} por profissional, mais ${formatBRL(TEAM_PLAN.perClientBlock)} a cada ${TEAM_PLAN.clientBlockSize} clientes; a partir de ${formatBRL(TEAM_PLAN.fromPrice)} por mês.`,
  );
  return [
    ...lines,
    '',
    `O preço de referência é o do site. No iPhone a assinatura é feita pela App Store e o valor pode ser diferente. Planos mensais, sem fidelidade; no iPhone, cancela-se pelos Ajustes da Apple. App Store: ${PLATFORMS.appStoreUrl}. Google Play: ${PLATFORMS.playStoreUrl}`,
  ].join('\n');
}

export function paymentBlock(siteUrl: string): string {
  return [
    `- Por fora, em qualquer plano: o cliente paga direto ao profissional (Pix, dinheiro, maquininha) e o valor não passa pela Controlapag. O profissional toca em "Confirmar recebimento" e confirma.`,
    `- Pelo link, nos planos pagos e só se o profissional ativar: o cliente paga com Pix, cartão (${joinPt(PAYMENT_FEES.cardBrands)}, à vista) ou boleto e a cobrança dá baixa sozinha. Taxa por pagamento: Pix ${pct(PAYMENT_FEES.pixPercent)}; cartão ${pct(PAYMENT_FEES.cardPercent)} + ${formatBRL(PAYMENT_FEES.cardFixed)}; boleto ${formatBRL(PAYMENT_FEES.boletoFixed)}. O Pix fica disponível em ${PAYMENT_FEES.pixSettlementBusinessDays} dias úteis e o cartão em ${PAYMENT_FEES.cardSettlementDays} dias. A Controlapag não fica com parte do pagamento. Detalhes: ${siteUrl}/ajuda/recebimentos`,
  ].join('\n');
}

export const FOR_END_CLIENT =
  'Conta grátis e opcional: o cliente vê num lugar só as cobranças, os pagamentos, os contratos e os horários com o profissional, envia comprovante, remarca e cancela dentro do prazo, e paga pelo link. Para agendar sem conta, basta confirmar o telefone com um código pelo WhatsApp. Marcar sozinho só funciona quando o profissional tem o perfil público (planos pagos).';

/** Páginas fixas: [caminho, nome, o que responde]. Só entra se a rota existir (o teste confere). */
export const FIXED_PAGES: [string, string, string][] = [
  ['/', 'Página inicial', 'o que o app faz, planos e perguntas frequentes'],
  ['/guias', 'Guias', 'guias práticos para autônomo e as comparações'],
  ['/ajuda/recebimentos', 'Receber pela Controlapag', 'recebimento pelo link, taxas, prazos, limites e reembolso'],
  ['/buscar', 'Buscar profissionais', 'diretório de quem atende pela Controlapag'],
  ['/register', 'Criar conta grátis', 'cadastro do profissional'],
  ['/sobre', 'Sobre a Controlapag', 'quem faz, onde fica e para quem é'],
  ['/contato', 'Contato', 'e-mail, horário e prazo de resposta'],
  ['/suporte', 'Suporte', 'dúvidas de uso, atendimento por e-mail'],
  ['/terms', 'Termos de uso', 'assinatura, renovação e cancelamento'],
  ['/privacidade', 'Privacidade', 'tratamento de dados conforme a LGPD'],
];
